import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const LOGO_SRC = `${process.env.PUBLIC_URL}/logo.png`;

/**
 * Official SolveTax brand wordmark — light header shows it directly;
 * the dark footer wraps it in a white plate with the tagline below.
 */
const BrandLogo = ({
  asLink = true,
  showTagline = false,
  variant = 'default',
  className = '',
  imgClassName = '',
}) => {
  const { t } = useLanguage();

  const imgSizes = {
    default: 'h-[22px] sm:h-[24px] md:h-[26px] w-auto max-w-[min(170px,48vw)]',
    footer: 'h-[34px] sm:h-[38px] w-auto max-w-[220px]',
  };

  const imgSize = imgClassName || imgSizes[variant] || imgSizes.default;

  const mark = (
    <span
      className={`brand-logo-frame block leading-none ${
        variant === 'footer' ? 'brand-logo-frame--plate' : 'brand-logo-frame--light'
      }`}
      aria-hidden={false}
    >
      <img
        src={LOGO_SRC}
        alt={t('brand.logo_alt', 'SolveTax')}
        className={`brand-logo-img block object-contain object-left ${imgSize}`}
        width={521}
        height={99}
        decoding="async"
        fetchPriority={variant === 'default' ? 'high' : 'auto'}
        draggable={false}
      />
    </span>
  );

  const taglineClasses =
    variant === 'footer'
      ? 'brand-logo-tagline brand-logo-tagline--footer block text-[9px] sm:text-[10px] font-medium uppercase tracking-[0.16em] leading-snug text-teal-100/90 mt-2 pl-4'
      : 'brand-logo-tagline brand-logo-tagline--header block text-[8px] sm:text-[9px] md:text-[10px] font-medium uppercase tracking-[0.14em] sm:tracking-[0.16em] leading-snug text-gray-500 mt-1 sm:mt-1.5 w-full';

  const tagline = showTagline ? (
    <span className={taglineClasses}>{t('nav.tagline', 'Your Finances, Our Priority')}</span>
  ) : null;

  const markBlock = variant === 'footer' ? (
    <span
      className={`brand-logo-lockup brand-logo-lockup--footer inline-flex flex-col items-start min-w-0 max-w-full ${className}`}
    >
      <span className="brand-logo-plate inline-block rounded-2xl bg-white px-4 pt-3 pb-3.5 overflow-hidden border-0 shadow-[0_8px_28px_rgba(0,0,0,0.2)]">
        {mark}
      </span>
      {tagline}
    </span>
  ) : (
    <span
      className={`brand-logo-lockup brand-logo-lockup--header inline-flex flex-col items-start justify-center min-w-0 max-w-full ${className}`}
    >
      {mark}
      {tagline}
    </span>
  );

  if (!asLink) {
    return markBlock;
  }

  return (
    <Link
      to="/"
      className="group shrink-0 min-w-0 max-w-[58vw] sm:max-w-none rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14A098] focus-visible:ring-offset-2"
      aria-label={t('brand.home', 'SolveTax home')}
    >
      <span className="inline-block transition-transform duration-200 group-hover:scale-[1.01] group-active:scale-[0.99]">
        {markBlock}
      </span>
    </Link>
  );
};

export default BrandLogo;
